/**
 * One definition of the basemap for every map in the console.
 *
 * Four components each hardcoded their own CARTO raster URLs. Those URLs carry
 * no API key, and CARTO now stamps "API KEY REQUIRED" across keyless raster
 * tiles — so every map in the product rendered the watermark, in four places
 * that had to be fixed independently.
 *
 * Provider is chosen at build time:
 *
 *   VITE_BASEMAP_PROVIDER=carto  + VITE_CARTO_API_KEY=<key>
 *       CARTO's light/dark label-split basemap, the original design.
 *   VITE_BASEMAP_URL=<template>  (optional VITE_BASEMAP_LABELS_URL)
 *       Any raster XYZ endpoint, for a self-hosted or paid provider.
 *   (nothing set — the default)
 *       OpenStreetMap raster tiles. No key, no watermark, single layer.
 *
 * Note these are Vite env vars: they are inlined at build time, so changing
 * them means rebuilding the dashboard image, not editing compose.
 */

export type MapTheme = "light" | "dark";

export interface BasemapLayers {
  /**
   * True when the tiles are a full-colour general-purpose map rather than a
   * muted cartography designed to sit under data. Overlays are drawn in the
   * operator's own colours — the Orange Line really is orange — and standard
   * OSM paints trunk roads in very nearly that same orange, so the route line
   * disappeared into the road it follows. The map desaturates these tiles in
   * CSS so overlays read against them.
   */
  muted: boolean;
  /** Base raster layer. */
  base: { url: string; subdomains?: string; attribution: string };
  /**
   * Optional label layer drawn above the data. Present only for providers
   * that split labels from the base imagery (CARTO does; OSM does not).
   */
  labels?: { url: string; subdomains?: string };
}

const CARTO_KEY = import.meta.env.VITE_CARTO_API_KEY as string | undefined;
const CUSTOM_URL = import.meta.env.VITE_BASEMAP_URL as string | undefined;
const CUSTOM_LABELS_URL = import.meta.env.VITE_BASEMAP_LABELS_URL as string | undefined;
const PROVIDER = (import.meta.env.VITE_BASEMAP_PROVIDER as string | undefined)?.toLowerCase();

const OSM_ATTRIBUTION = "&copy; OpenStreetMap contributors";
const CARTO_ATTRIBUTION = "&copy; OpenStreetMap &copy; CARTO";

function cartoUrl(style: string): string {
  const key = CARTO_KEY ? `?api_key=${encodeURIComponent(CARTO_KEY)}` : "";
  return `https://{s}.basemaps.cartocdn.com/${style}/{z}/{x}/{y}{r}.png${key}`;
}

export function basemapLayers(theme: MapTheme): BasemapLayers {
  if (CUSTOM_URL) {
    return {
      muted: false,
      base: { url: CUSTOM_URL, attribution: OSM_ATTRIBUTION },
      labels: CUSTOM_LABELS_URL ? { url: CUSTOM_LABELS_URL } : undefined,
    };
  }

  // Only use CARTO when a key exists — without one every tile is watermarked,
  // which is worse than a plainer basemap that actually renders.
  if (PROVIDER === "carto" && CARTO_KEY) {
    const styles =
      theme === "light"
        ? { base: "light_nolabels", labels: "light_only_labels" }
        : { base: "dark_nolabels", labels: "dark_only_labels" };
    // CARTO's *_nolabels styles are already built to sit under data.
    return {
      muted: true,
      base: { url: cartoUrl(styles.base), subdomains: "abcd", attribution: CARTO_ATTRIBUTION },
      labels: { url: cartoUrl(styles.labels), subdomains: "abcd" },
    };
  }

  return {
    muted: false,
    base: {
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      subdomains: "abc",
      attribution: OSM_ATTRIBUTION,
    },
  };
}

/**
 * Key for the <TileLayer> element. Leaflet does not swap a layer's URL in
 * place, so the layer has to be remounted when the theme changes.
 */
export function basemapKey(theme: MapTheme): string {
  return `${PROVIDER ?? "default"}-${theme}`;
}
